// Platformverschillen tussen desktop, web, iOS en Android.
//
// Alles hier is optioneel: elke call zit apart in een try/catch, zodat een
// commando dat op een bepaald platform niet bestaat nooit het laden van de
// activity kan breken. Een activity die niet opent is een veel groter
// probleem dan een orientatieslot dat niet pakt.

import { Common, Platform } from '@discord/embedded-app-sdk'
import type { DiscordSDK } from '@discord/embedded-app-sdk'
import { describeError, log } from './log'

export interface PlatformInfo {
  platform: Platform
  isMobile: boolean
  /** Op iOS schuift de view soms mee met het toetsenbord, soms niet. Dat
   *  bepaalt straks hoe het invoerveld van het spel zich moet gedragen. */
  iosKeyboardResizesView: boolean
}

const THERMAL_LABEL: Record<number, string> = {
  [-1]: 'onbekend',
  0: 'nominal',
  1: 'fair',
  2: 'serious',
  3: 'critical',
}

/** Voert iets uit dat best mag mislukken, en logt het als het misgaat. */
async function attempt(what: string, action: () => Promise<unknown>): Promise<boolean> {
  try {
    await action()
    return true
  } catch (error) {
    log('warn', `${what} lukte niet: ${describeError(error)}`)
    return false
  }
}

export async function setupPlatform(sdk: DiscordSDK): Promise<PlatformInfo> {
  const isMobile = sdk.platform === Platform.MOBILE
  log('info', `Platform: ${sdk.platform}`)

  const info: PlatformInfo = {
    platform: sdk.platform,
    isMobile,
    iosKeyboardResizesView: false,
  }

  await attempt('getPlatformBehaviors', async () => {
    const behaviors = await sdk.commands.getPlatformBehaviors()
    info.iosKeyboardResizesView = behaviors.iosKeyboardResizesView ?? false
    log('info', `iosKeyboardResizesView: ${info.iosKeyboardResizesView}`)
  })

  if (isMobile) {
    // Staand vastzetten. Het spel is een lijst met woorden plus een timer,
    // dat leest prettiger in portrait dan in landscape. Omzetten kan later
    // met LANDSCAPE of UNLOCKED.
    await attempt('setOrientationLockState', () =>
      sdk.commands.setOrientationLockState({
        lock_state: Common.OrientationLockStateTypeObject.PORTRAIT,
        picture_in_picture_lock_state: Common.OrientationLockStateTypeObject.PORTRAIT,
      }),
    )
  }

  // Op een telefoon die warm wordt knijpt het systeem de performance af. Nu
  // alleen loggen, later kunnen we hierop animaties terugschroeven.
  await attempt('THERMAL_STATE_UPDATE abonneren', () =>
    sdk.subscribe('THERMAL_STATE_UPDATE', (event) => {
      log('info', `Thermische staat: ${THERMAL_LABEL[event.thermal_state] ?? event.thermal_state}`)
    }),
  )

  // De activity kan van fullscreen naar picture-in-picture springen. Daar
  // moet de layout straks op reageren.
  await attempt('ACTIVITY_LAYOUT_MODE_UPDATE abonneren', () =>
    sdk.subscribe('ACTIVITY_LAYOUT_MODE_UPDATE', (event) => {
      log('info', `Layout mode: ${event.layout_mode}`)
    }),
  )

  await attempt('ORIENTATION_UPDATE abonneren', () =>
    sdk.subscribe('ORIENTATION_UPDATE', (event) => {
      log('info', `Orientatie: ${event.screen_orientation}`)
    }),
  )

  return info
}
